import {
  Button,
  Card,
  Form,
  Input,
  Select,
  Tabs,
  TimePicker,
  message,
} from "antd";
import { useEffect, useState } from "react";
import { useForm } from "antd/es/form/Form";
import { useNavigate } from "@tanstack/react-router";
import { ICourse } from "./list";
import useBucketStore from "../store";
import useCourseStore from "../store/course";
import { IVideo } from "../interfaces";
import { apiBaseURL, studioApi } from "../api";
import { ArrowLeftOutlined } from "@ant-design/icons";

type TLesson = {
  label: string;
  value: string;
};

type TYoutubeChannel = {
  _id: string;
  channel_id: string;
  channel_title: string;
  email: string;
};

export default function GoLive() {
  const [title, setTitle] = useState("");

  const [filteredCourses, setFilteredCourses] = useState<ICourse[]>([]);

  const [selectedCourseId, setSelectedCourseId] = useState("");

  const [selectedCourse, setSelectedCourse] = useState({} as ICourse);

  const [lessons, setLessons] = useState<TLesson[]>([]);

  const [selectedLesson, setSelectedLesson] = useState("");

  const [bucketId, setBucketId] = useState("");

  const [platform, setPlatform] = useState<"kgs" | "youtube">("kgs");

  const [channels, setChannels] = useState<TYoutubeChannel[]>([]);

  const [selectedChannel, setSelectedChannel] = useState<any>(null);
  const [scheduleAt, setScheduleAt] = useState("");

  const { buckets } = useBucketStore();

  const navigate = useNavigate({ from: "/list" });

  const { courses } = useCourseStore();

  const [form] = useForm();

  useEffect(() => {
    setFilteredCourses(courses);
  }, [courses]);

  useEffect(() => {
    fetchYoutubeChannels();
  }, []);

  const fetchYoutubeChannels = async () => {
    try {
      const { data } = await studioApi.get("/youtube");

      setChannels(data || []);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch youtube channels");
    }
  };

  const handleSelectCourse = async (value: string) => {
    setSelectedCourseId(value);

    const course = filteredCourses.find((c) => c.id === value);

    setSelectedCourse(course!);

    try {
      const { data } = await apiBaseURL.get<{ id: number; name: string }[]>(
        `lessons/v2/${value}`
      );

      const formattedLessons = data.map((lesson) => ({
        label: `${lesson.id} - ${lesson.name}`,
        value: String(lesson.id),
      }));

      setLessons(formattedLessons);
    } catch (err) {
      message.error("Failed to fetch lessons");
    }
  };

  const handleScheduleClass = async () => {
    const vid = "studio-" + (Math.random() + 1).toString(36).substring(2);

    try {
      if (platform === "kgs") {
        const payload = {
          title,
          vid,
          course_name: selectedCourse.title,
          course_id: String(selectedCourseId),
          lesson_id: selectedLesson,
          schedule_at: scheduleAt,
          bucket: bucketId,
          videoType: "kgs",
        };

        const { data } = await studioApi.post<IVideo>("videos", payload);

        form.resetFields();

        navigate({
          to: `/studio/${data._id}`,
        });

        return;
      }
      if (platform === "youtube") {
        if (!selectedChannel) {
          message.warning("Please select youtube channel");

          return;
        }

        const payload = {
          title,
          vid,
          course_name: selectedCourse.title,
          course_id: String(selectedCourseId),
          lesson_id: selectedLesson,
          schedule_at: scheduleAt,
          youtube_channel_id: selectedChannel.channel_id,

          videoType: "youtube",
        };

        const { data } = await studioApi.post(
          `/youtube/${selectedChannel._id}/schedule`,
          payload
        );

        form.resetFields();

        message.success("Youtube live scheduled successfully");
        navigate({
          to: `/studio/${data?.data?.video?._id}`,
        });
        return;
      }
    } catch (error: any) {
      console.log(error);

      message.error(
        error?.response?.data?.message || "Failed to schedule live"
      );
    }
  };
  return (
    <Card
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => window.history.back()}
          />
          <span>Schedule Live Class</span>
        </div>
      }
      type="inner"
    >
      <Form form={form} layout="vertical" onFinish={handleScheduleClass}>
        <Tabs
          activeKey={platform}
          onChange={(key) => setPlatform(key as "kgs" | "youtube")}
          items={[
            {
              key: "kgs",
              label: "KGS",
            },
            {
              key: "youtube",
              label: "Youtube",
            },
          ]}
        />
        <Form.Item
          label="Class Title"
          name="title"
          rules={[
            {
              required: true,
              message: "Please give title to your class!",
            },
          ]}
        >
          <Input onChange={(event) => setTitle(event.target.value)} />
        </Form.Item>
        <Form.Item label="Select Course" name="course">
          <Select
            showSearch
            placeholder="Select the course"
            onSelect={handleSelectCourse}
            options={filteredCourses.map((course) => ({
              label: `${course.id} - ${course.title}`,
              value: course.id,
            }))}
            filterOption={(input, option) =>
              option?.label
                ?.toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item label="Select lesson" name="lesson">
          <Select
            showSearch
            placeholder="Select lesson"
            onSelect={(value) => {
              setSelectedLesson(value);
            }}
            options={lessons}
          />
        </Form.Item>
        {platform === "kgs" && (
          <Form.Item label="Select bucket" name="bucket">
            <Select
              showSearch
              placeholder="Select bucket"
              fieldNames={{
                label: "title",
                value: "_id",
              }}
              onSelect={(value) => {
                setBucketId(value);
              }}
              options={buckets}
            />
          </Form.Item>
        )}
        {platform === "youtube" && (
          <Form.Item label="Select Youtube Channel" name="channel">
            <Select
              showSearch
              placeholder="Select youtube channel"
              options={channels.map((channel) => ({
                label: channel.channel_title,
                value: channel._id,
              }))}
              onSelect={(value) => {
                const channel = channels.find((c) => c._id === value);

                setSelectedChannel(channel);
              }}
            />
          </Form.Item>
        )}
        <Form.Item label="Scheduled Time" name="time">
          <TimePicker
            className="w-full"
            onChange={(time) => {
              if (time) {
                setScheduleAt(time.toISOString());
              }
            }}
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" size="large">
            Go Live
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}
